import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { FormControlLabel, IconButton, Radio, RadioGroup } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';

import { AppConfig } from '../../../AppConfig';
import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { PageDialog } from '../../../common/components/PageDialog';
import TableCard from '../../../common/components/tables/TableCard';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { AppHelper } from '../../../helpers/AppHelper';
import { CRMHelper } from '../../../helpers/CRMHelper';
import { generateRuleJobsReport, sanitizeAutomationDetailFormPayload } from './AutomationDetail';
import { AutomationFlowCanvas } from './automation-journey/AutomationFlowCanvas';
import { nodePreview } from './automation-journey/automationNodePreview';
import { AutomationNodePalette } from './automation-journey/AutomationNodePalette';
import { ChatbotConfirmDialog } from './whatsapp-chatbot/ChatbotConfirmDialog';
import {
  NODE_TYPE_ACTION,
  NODE_TYPE_STOP,
  NODE_TYPE_TRIGGER,
  addActionNode,
  addConnectedActionNode,
  addEdge,
  applyBotJsonFields,
  automationFromRule,
  botJsonFromAutomation,
  duplicateNode,
  removeEdge,
  removeNode,
  toFlowEdges,
  toFlowNodes,
  unconnectedActionIds,
  updateNodePosition,
  withRowOrders,
} from './automation-journey/automationGraph';

const GRAPH_LOAD_ERROR_MSG = 'Unable to load the automation flow. Refresh the page to try again.';

function entryConditionRowFields(fieldDefRuleFilters) {
  const steps = fieldDefRuleFilters && fieldDefRuleFilters.steps;
  const fields = steps && steps[0] && Array.isArray(steps[0].fields) ? steps[0].fields : [];
  const rowFields = [];
  fields.forEach(field => {
    if (!field || !field.key) return;
    if (field.hidden || field.type === 'hidden') return;
    const copy = JSON.parse(JSON.stringify(field));
    if (copy.type === 'select') {
      copy.type = copy.multiSelect ? 'check_box' : 'radio';
    }
    rowFields.push(copy);
  });
  return rowFields;
}

function rowConditionMatches(row, condition) {
  if (!condition || !row) return true;
  if (Array.isArray(condition.or)) {
    return condition.or.some(clause => rowConditionMatches(row, clause));
  }
  const statements = Array.isArray(condition.and) ? condition.and : [condition];
  for (let i = 0; i < statements.length; i++) {
    const clause = statements[i];
    if (!clause || !clause.key) return false;
    const conditionState = row[clause.key];
    const conditionValues = clause.values || [clause.value];
    const isConditionStateInValues = Array.isArray(conditionState)
      ? conditionState.some(item => Helper.inArray(conditionValues, item))
      : Helper.inArray(conditionValues, conditionState);
    const conditionOp = clause.op || 'is';
    if (conditionState === undefined) return false;
    if (conditionOp === 'is' && !isConditionStateInValues) return false;
    if (conditionOp === 'not' && isConditionStateInValues) return false;
    const hasValue = Array.isArray(conditionState) ? conditionState.length > 0 : !!conditionState;
    if (conditionOp === 'notnull' && !hasValue) return false;
  }
  return true;
}

function matchingValueField(row, rowFields) {
  return (rowFields || []).find(field => {
    if (!field || field.key !== 'value') return false;
    if (!field.condition) return true;
    return rowConditionMatches(row, field.condition);
  }) || null;
}

function dialogFilterRow(row, rowFields) {
  const next = { ...(row || {}) };
  const valueField = matchingValueField(next, rowFields);
  if (valueField && valueField.type !== 'check_box' && Array.isArray(next.value) && next.value.length === 1) {
    next.value = next.value[0];
  }
  return next;
}

function persistFilterRow(row) {
  if (!row || typeof row !== 'object') return null;
  const next = {};
  Object.keys(row).forEach(key => {
    if (key === 'id' && String(row.id).indexOf('lr_') === 0) return;
    const value = row[key];
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const items = value.filter(item => item != null && item !== '');
      if (!items.length) return;
      next[key] = items;
      return;
    }
    next[key] = value;
  });
  const keys = Object.keys(next).filter(key => key !== 'id');
  if (!keys.length) return null;
  return next;
}

function dialogStopRows(rows, rowFields) {
  const list = (Array.isArray(rows) ? rows : [])
    .filter(row => row && typeof row === 'object' && !Array.isArray(row))
    .map(row => dialogFilterRow(CRMHelper.normalizeAutomationAssigneeCurrent(row), rowFields));
  return Helper.sortDictArray(list);
}

function stopRowsFromRule(rule, rowFields) {
  const stops = rule && rule.stops;
  if (!stops || typeof stops !== 'object') return [];
  return dialogStopRows(Object.values(stops), rowFields);
}

function stopRowsForSettings(graph, rule, rowFields) {
  const nodes = (graph && graph.nodes) || [];
  const stop = nodes.find(node => node && node.type === NODE_TYPE_STOP);
  const exitConditions = stop && stop.data && stop.data.exitConditions;
  if (Array.isArray(exitConditions)) return dialogStopRows(exitConditions, rowFields);
  return stopRowsFromRule(rule, rowFields);
}

function exitConditionsFromForm(formRows) {
  const nextRows = (Array.isArray(formRows) ? formRows : [])
    .map(row => persistFilterRow(row))
    .filter(Boolean);
  const orderItems = [];
  nextRows.forEach(row => {
    if (row.id && row.order) orderItems.push({ order: row.order });
  });
  nextRows.forEach(row => {
    if (row.id && row.order) return;
    const duplicate = row.order && orderItems.some(item => String(item.order) === String(row.order));
    if (!row.order || duplicate) {
      row.order = AppHelper.generateNewOrder(orderItems);
    }
    orderItems.push({ order: row.order });
  });
  return nextRows;
}

function AutomationResultsTable(props) {
  return (
    <TableCard
      item={{ data: props.report || {}, title: '', maxRows: props.maxRows }}
      showMoreInTable
      nextPage={props.nextPage}
      nextPageState={props.nextPageState}
      onNext={props.onNext}
      cellWidths={props.cellWidths}
      tableRowActions={props.tableRowActions}
    />
  );
}

function graphWithTriggerEvent(graph, eventType) {
  let updated = false;
  const nodes = ((graph && graph.nodes) || []).map(node => {
    if (updated || !node || node.type !== NODE_TYPE_TRIGGER) return node;
    updated = true;
    return {
      ...node,
      data: {
        ...(node.data || {}),
        eventType: eventType || '',
        filters: [],
      },
    };
  });
  return { ...(graph || {}), nodes };
}

function actionFormDefinition(fieldDef, title) {
  const copy = JSON.parse(JSON.stringify(fieldDef));
  copy.title = title;
  const steps = copy.steps || [];
  steps.forEach(step => {
    const fields = step && step.fields;
    if (!fields) return;
    fields.forEach(field => {
      if (!field || field.key !== 'order') return;
      field.hidden = true;
      if (field.defaultValue == null || field.defaultValue === '') field.defaultValue = '010';
    });
  });
  return copy;
}

function actionDataFromForm(formData, formType) {
  const sanitized = sanitizeAutomationDetailFormPayload(
    Object.assign({}, formData || {}, { entityAttrName: 'actions' }),
    formType
  );
  delete sanitized.entityAttrName;
  delete sanitized.order;
  delete sanitized.REMOVE_ATTRS;
  return sanitized;
}

function conditionOp(value) {
  return value === 'OR' ? 'OR' : 'AND';
}

function conditionOpHeaderActions(fieldKey) {
  return ({ getValue, setValue }) => {
    const selectedOp = getValue(fieldKey) === 'OR' ? 'OR' : 'AND';
    return (
      <RadioGroup
        row
        value={selectedOp}
        onChange={(event) => setValue(fieldKey, event.target.value)}
        className={css(Styles.filtersOpActionsContainer)}
      >
        <FormControlLabel
          value='AND'
          control={<Radio color='primary' size='small' />}
          label='AND'
          className={css(Styles.filtersOpActionLabel)}
        />
        <FormControlLabel
          value='OR'
          control={<Radio color='primary' size='small' />}
          label='OR'
          className={css(Styles.filtersOpActionLabel)}
        />
      </RadioGroup>
    );
  };
}

function hydrateSuffixedFields(record, fieldDef) {
  const steps = fieldDef && fieldDef.steps;
  const fields = steps && steps[0] && steps[0].fields;
  if (!record || !Array.isArray(fields)) return;
  fields.forEach(field => {
    if (!field || !field.key) return;
    const key = String(field.key);
    const split = key.indexOf('__');
    if (split <= 0) return;
    if (record[key] != null && record[key] !== '') return;
    const bare = key.slice(0, split);
    if (record[bare] == null || record[bare] === '') return;
    record[key] = record[bare];
  });
}

function actionRecord(data, fieldDef) {
  const record = CRMHelper.normalizeAutomationAssigneeCurrent({ ...(data || {}) });
  hydrateSuffixedFields(record, fieldDef);
  delete record.actionLabel;
  delete record.detailLine;
  delete record.scheduleLine;
  delete record.onDelete;
  delete record.onDuplicate;
  delete record.order;
  return record;
}

export class AutomationJourneyDetail extends BaseEntityPageSlixta {
  constructor(props) {
    super(props);
    this.pageTitle = 'Automation Journey';
    this.ms = AppConfig.CDP_MS.ENDPOINT;
    this.orgId = this.props.match.params.orgId || 'PRODUCT';
    this.ruleId = this.props.match.params.id;
    this.breadCrumbs = [
      { title: Helper.getString('backToAutomations'), to: this.baseRoute() + '/automations', type: 'button', icon: 'arrow_back_ios' },
    ]
    this.ruleJobsLimit = 200;
    this.toFetch = [
      { key: 'rule', ms: this.ms, method: 'GET', paths: ['rules', this.ruleId], queryParams: { orgId: this.orgId, pb: 'fieldDef,fieldDefRuleFilters,fieldDefRuleStops,fieldDefRuleActions' } },
      { key: 'ruleJobs', ms: this.ms, method: 'GET', paths: ['rulejobs'], queryParams: { orgId: this.orgId, ruleId: this.ruleId, limit: this.ruleJobsLimit } },
      // same staff list as the old AutomationDetail page, so assignee options / labels match
      { key: 'usersORG', ms: this.ms, method: 'GET', paths: ['miscs', 'get-org-users2'], queryParams: { orgId: this.orgId, parentOrgId: this.orgId } },
    ]
    this.apiData = {
      orgId: this.orgId
    }
    this.pageKey = this.pageTitle;
    this.noAdd = true;
    this.graph = null;
    this.flowNodes = null;
    this.flowEdges = null;
    this.editingNodeId = null;
    this.dialogMode = null;
    this.fieldDefRequestId = 0;
    this.filtersByEventType = {};
    this.saving = false;
    this.dirty = false;
    this.graphLoadError = false;
    this.allowLeave = false;
    this.pendingLocation = null;
    this.pendingAction = null;
    this.pendingRefreshCallback = null;
    this.pendingActionConnect = null;
    this.ruleJobs = [];
    this.ruleJobsNextPage = null;
    this.staffUsers = [];
    this.setHeaderActions();
  }

  setHeaderActions() {
    const actions = [];
    const state = this.rule && this.rule.state;
    if (state === 'DISABLED') {
      actions.push({
        label: 'Start',
        tooltip: 'Start',
        icon: 'send',
        type: 'icon-label',
        onClick: () => this.updateRuleState('LIVE'),
      });
    }
    if (state === 'LIVE') {
      actions.push({
        label: 'Pause',
        tooltip: 'Pause',
        icon: 'pause',
        type: 'icon-label',
        onClick: () => this.updateRuleState('DISABLED'),
      });
    }
    if (Array.isArray(this.ruleJobs) && this.ruleJobs.length > 0) {
      actions.push({
        label: 'Results',
        tooltip: 'Results',
        icon: 'trending_up',
        type: 'icon-label',
        onClick: () => this.setState({ resultsOpen: true }),
      });
    }
    if (this.rule) {
      actions.push({
        label: 'Settings',
        tooltip: 'Settings',
        icon: 'settings',
        type: 'icon-label',
        onClick: () => this.openSettings(),
      });
    }
    actions.push({
      label: 'Refresh',
      tooltip: 'Refresh',
      icon: 'refresh',
      type: 'icon-label',
      onClick: () => this.fetchItems(),
    });
    this.headerActions = actions;
  }

  updateRuleState(nextState) {
    if (!this.rule || !this.ruleId || this.saving) return;
    if (nextState === 'LIVE' && unconnectedActionIds(this.graph).length) {
      this.setState({
        showToast: true,
        isErrorToast: true,
        toastMsg: 'All actions must be connected in the flow before the automation can be LIVE',
      });
      return;
    }
    this.saving = true;
    this.setState({ saving: true });
    ApiHelper.call(
      {
        method: 'PUT',
        endPoint: this.ruleEndPoint(),
        jsonBody: { orgId: this.orgId, state: nextState },
      },
      (err) => {
        this.saving = false;
        if (err) {
          this.setState({
            saving: false,
            showToast: true,
            isErrorToast: true,
            toastMsg: Helper.getErrorMsg(err),
          });
          return;
        }
        this.rule.state = nextState;
        this.setHeaderActions();
        this.setState({
          saving: false,
          showToast: true,
          isErrorToast: false,
          toastMsg: nextState === 'LIVE' ? 'Automation started.' : 'Automation paused.',
        });
      }
    );
  }

  componentDidMount() {
    super.componentDidMount();
    this.unblock = this.props.history.block((location, action) => {
      if (!this.dirty || this.allowLeave) return true;
      this.pendingLocation = location;
      this.pendingAction = action;
      this.pendingRefreshCallback = null;
      this.setState({ leaveConfirmOpen: true });
      return false;
    });
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  componentWillUnmount() {
    if (this.unblock) this.unblock();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.modalOpen && !this.state.modalOpen && this.dialogMode === 'addAction') {
      this.pendingActionConnect = null;
      this.dialogMode = null;
    }
  }

  onBeforeUnload = (e) => {
    if (!this.dirty) return;
    e.preventDefault();
    e.returnValue = '';
  }

  fetchItems(onReadyCallback) {
    if (this.dirty) {
      this.pendingLocation = null;
      this.pendingAction = 'REFRESH';
      this.pendingRefreshCallback = onReadyCallback;
      this.setState({ leaveConfirmOpen: true });
      return;
    }
    super.fetchItems(onReadyCallback);
  }

  closeLeaveConfirm = () => {
    this.pendingLocation = null;
    this.pendingAction = null;
    this.pendingRefreshCallback = null;
    this.setState({ leaveConfirmOpen: false });
  }

  discardLeaveConfirm = () => {
    const loc = this.pendingLocation;
    const action = this.pendingAction;
    const refreshCb = this.pendingRefreshCallback;
    this.pendingLocation = null;
    this.pendingAction = null;
    this.pendingRefreshCallback = null;
    this.dirty = false;
    this.setState({ leaveConfirmOpen: false });
    if (action === 'REFRESH') {
      super.fetchItems(refreshCb);
      return;
    }
    this.allowLeave = true;
    if (this.unblock) this.unblock();
    if (action === 'POP') {
      this.props.history.goBack();
      return;
    }
    if (loc) this.props.history.push(loc);
  }

  onFetchSuccess(results) {
    this.fieldDefRequestId = this.fieldDefRequestId + 1;
    const rule = this.getResult(results, 'rule');
    this.rule = rule;
    const usersOrgRes = this.getResult(results, 'usersORG');
    this.staffUsers = (usersOrgRes && Array.isArray(usersOrgRes.items)) ? usersOrgRes.items : [];
    this.fieldDef = rule && rule.fieldDef;
    this.fieldDefRuleStops = rule && rule.fieldDefRuleStops;
    this.fieldDefRuleActions = rule && rule.fieldDefRuleActions;
    this.fieldDefRuleFilters = rule && rule.fieldDefRuleFilters;
    this.applyAssigneeOptions();
    const eventType = (rule && rule.eventType) || '';
    this.filtersByEventType = {};
    if (eventType && this.fieldDefRuleFilters) {
      this.filtersByEventType[eventType] = this.fieldDefRuleFilters;
    }
    this.setGraphFromRule(rule);
    this.dirty = false;
    const jobs = this.getResult(results, 'ruleJobs');
    this.ruleJobs = (jobs && Array.isArray(jobs.items)) ? jobs.items : [];
    this.ruleJobsNextPage = jobs ? jobs.nextPage : null;
    if (rule && rule.title) {
      this.pageTitle = rule.title;
      this.pageKey = rule.title;
    } else {
      this.pageTitle = 'Automation Journey';
      this.pageKey = this.pageTitle;
    }
    this.setHeaderActions();
    this.syncCanvas();
    if (this.graphLoadError) this.showGraphLoadError();
  }

  setGraphFromRule(rule) {
    this.graph = automationFromRule(rule);
    this.graphLoadError = !!(this.graph && this.graph.incomplete);
  }

  showGraphLoadError() {
    this.setState({
      showToast: true,
      isErrorToast: true,
      toastMsg: GRAPH_LOAD_ERROR_MSG,
    });
  }

  /** Saving a graph that did not load would overwrite the rule's filters / actions / stops. */
  graphSaveBlocked(caller) {
    if (!this.graphLoadError) return false;
    if (caller) caller.setState({ inProcess: false, success: false, message: GRAPH_LOAD_ERROR_MSG });
    else this.showGraphLoadError();
    return true;
  }

  /** Replace the local graph with the server's after a save (BE assigns row ids / order). */
  async reloadRuleGraph() {
    const rule = await ApiHelper.callAwait({
      method: 'GET',
      endPoint: this.ruleEndPoint({ pb: 'fieldDef,fieldDefRuleFilters,fieldDefRuleStops,fieldDefRuleActions' }),
    });
    this.applySettingsRefresh(rule);
  }

  syncCanvas() {
    if (!this.graph) {
      this.flowNodes = null;
      this.flowEdges = null;
      return;
    }
    const stopIds = {};
    (this.graph.nodes || []).forEach(node => {
      if (node && node.type === NODE_TYPE_STOP) stopIds[node.id] = true;
    });
    this.flowNodes = toFlowNodes((this.graph.nodes || []).filter(node => node && node.type !== NODE_TYPE_STOP), (node) => {
      if (!node) return null;
      if (node.type === NODE_TYPE_TRIGGER) {
        const eventType = node.data && node.data.eventType;
        const filtersOp = conditionOp(node.data && node.data.filtersOp);
        return nodePreview(node.data, [this.fieldDef, this.filterFieldDefFor(eventType)], { filters: filtersOp });
      }
      if (node.type === NODE_TYPE_ACTION) {
        return nodePreview(node.data, this.fieldDefRuleActions);
      }
      return null;
    });
    this.flowEdges = toFlowEdges((this.graph.edges || []).filter(edge => {
      const source = edge && (edge.sourceNodeId || edge.source);
      const target = edge && (edge.targetNodeId || edge.target);
      return !stopIds[source] && !stopIds[target];
    }));
  }

  nodeById(nodeId) {
    const nodes = (this.graph && this.graph.nodes) || [];
    return nodes.find(node => node && node.id === nodeId) || null;
  }

  ruleEndPoint(queryParams) {
    return ApiHelper.makeUrlPath2({
      ms: this.ms,
      paths: ['rules', this.ruleId],
      queryParams: Object.assign({ orgId: this.orgId }, queryParams || {}),
    });
  }

  filterFieldDefFor(eventType) {
    if (eventType && this.filtersByEventType && Object.prototype.hasOwnProperty.call(this.filtersByEventType, eventType)) {
      return this.filtersByEventType[eventType];
    }
    return null;
  }

  eventTypeField() {
    const steps = this.fieldDef && this.fieldDef.steps;
    const sourceFields = steps && steps[0] && Array.isArray(steps[0].fields) ? steps[0].fields : [];
    const eventTypeField = sourceFields.find(field => field && field.key === 'eventType');
    if (!eventTypeField) return null;
    const eventTypeCopy = JSON.parse(JSON.stringify(eventTypeField));
    eventTypeCopy.label = 'Trigger Event';
    return eventTypeCopy;
  }

  triggerFormDefinition(eventType) {
    const fields = [];
    const eventTypeField = this.eventTypeField();
    if (eventTypeField) {
      eventTypeField.disabled = true;
      fields.push(eventTypeField);
    }
    const filterDef = this.filterFieldDefFor(eventType);
    fields.push({
      key: 'filtersOp',
      type: 'edit_text',
      hidden: true,
    });
    fields.push({
      key: 'filters',
      type: 'list',
      label: 'Filters',
      disabled: !filterDef,
      listConfig: {
        title: 'Filters',
        addButtonText: 'Add filter',
        minItems: 0,
        flatStorage: false,
        additionalListActions: conditionOpHeaderActions('filtersOp'),
        rowFields: entryConditionRowFields(filterDef),
      },
    });
    return {
      title: 'Edit Trigger',
      maxWidth: 'lg',
      steps: [{ fields }],
    };
  }

  changeTriggerFormDefinition() {
    const eventTypeField = this.eventTypeField();
    return {
      title: 'Change Trigger',
      steps: [{ fields: eventTypeField ? [eventTypeField] : [] }],
    };
  }

  triggerNode() {
    const nodes = (this.graph && this.graph.nodes) || [];
    return nodes.find(node => node && node.type === NODE_TYPE_TRIGGER) || null;
  }

  /** Same assignee options as the old AutomationDetail page (staff from get-org-users2). */
  applyAssigneeOptions() {
    CRMHelper.setUniqueAssigneeOptionsOnFieldDef(this.fieldDefRuleFilters, this.staffUsers);
    CRMHelper.setUniqueAssigneeOptionsOnFieldDef(this.fieldDefRuleActions, this.staffUsers);
    CRMHelper.setUniqueAssigneeOptionsOnFieldDef(this.fieldDefRuleStops, this.staffUsers);
  }

  applyFieldDefs(rule) {
    if (!rule) return;
    if (rule.fieldDef) this.fieldDef = rule.fieldDef;
    if (rule.fieldDefRuleStops) this.fieldDefRuleStops = rule.fieldDefRuleStops;
    if (rule.fieldDefRuleActions) this.fieldDefRuleActions = rule.fieldDefRuleActions;
    const eventType = rule.eventType || (this.rule && this.rule.eventType) || '';
    if (rule.fieldDefRuleFilters) {
      this.fieldDefRuleFilters = rule.fieldDefRuleFilters;
      if (!this.filtersByEventType) this.filtersByEventType = {};
      if (eventType) this.filtersByEventType[eventType] = rule.fieldDefRuleFilters;
    }
    this.applyAssigneeOptions();
    this.enableOpenFilterList(eventType);
    this.syncCanvas();
  }

  enableOpenFilterList(eventType) {
    if (this.dialogMode !== 'editTrigger' || !this.state.modalOpen || !this.formDefinition) return;
    const steps = this.formDefinition.steps;
    const fields = steps && steps[0] && steps[0].fields;
    const listField = fields && fields.find(field => field && field.key === 'filters' && field.type === 'list');
    if (!listField) return;
    const filterDef = this.filterFieldDefFor(eventType);
    listField.disabled = !filterDef;
    listField.listConfig = Object.assign({}, listField.listConfig, {
      rowFields: entryConditionRowFields(filterDef),
    });
  }

  refreshFieldDefs() {
    const requestId = this.fieldDefRequestId + 1;
    this.fieldDefRequestId = requestId;
    ApiHelper.call(
      {
        method: 'GET',
        endPoint: this.ruleEndPoint({ pb: 'fieldDef,fieldDefRuleFilters,fieldDefRuleStops,fieldDefRuleActions' }),
      },
      (err, data) => {
        if (this.fieldDefRequestId !== requestId) return;
        if (err) {
          this.showSaveError(err);
          return;
        }
        this.applyFieldDefs(data);
        this.setState({});
      }
    );
  }

  showSaveError(err) {
    this.setState({
      showToast: true,
      isErrorToast: true,
      toastMsg: Helper.getErrorMsg(err),
    });
  }

  openChangeTrigger() {
    const trigger = this.triggerNode();
    if (!trigger) return;
    this.dialogMode = 'changeTrigger';
    this.editingNodeId = null;
    const data = trigger.data || {};
    this.openModalForEdit({
      eventType: data.eventType || '',
    }, this.changeTriggerFormDefinition());
  }

  submitChangeTrigger(formData, caller) {
    const eventType = formData && formData.eventType ? formData.eventType : '';
    const trigger = this.triggerNode();
    const currentEvent = trigger && trigger.data ? trigger.data.eventType : '';
    if (this.graphSaveBlocked(caller)) return;
    if (!eventType || eventType === currentEvent) {
      this.dialogMode = null;
      if (caller) caller.setState({ inProcess: false, success: true, message: '' });
      this.setState({ modalOpen: false });
      return;
    }
    const nextGraph = applyBotJsonFields(graphWithTriggerEvent(this.graph, eventType), {
      filtersOp: conditionOp(this.rule && this.rule.filtersOp),
      stopsOp: conditionOp(this.rule && this.rule.stopsOp),
    });
    ApiHelper.call(
      {
        method: 'PUT',
        endPoint: this.ruleEndPoint(),
        jsonBody: {
          eventType,
          botJson: botJsonFromAutomation(nextGraph),
          filtersOp: conditionOp(this.rule && this.rule.filtersOp),
        },
      },
      (err) => {
        if (err) {
          if (caller) caller.setState({ inProcess: false, success: false, message: Helper.getErrorMsg(err) });
          return;
        }
        this.graph = nextGraph;
        this.dirty = false;
        if (this.rule) this.rule.eventType = eventType;
        this.syncCanvas();
        this.dialogMode = null;
        if (caller) caller.setState({ inProcess: false, success: true, message: '' });
        this.setState({ modalOpen: false });
        this.reloadRuleGraph().then(() => this.setState({})).catch(e => this.showSaveError(e));
      }
    );
  }

  openTriggerEditor(node) {
    if (!node || node.type !== NODE_TYPE_TRIGGER) return;
    const model = this.nodeById(node.id);
    if (!model) return;
    const data = model.data || {};
    const eventType = data.eventType || '';
    this.dialogMode = 'editTrigger';
    const formDefinition = this.triggerFormDefinition(eventType);
    const listField = formDefinition.steps[0].fields.find(field => field.key === 'filters');
    const rowFields = listField && listField.listConfig ? listField.listConfig.rowFields : [];
    const filters = (Array.isArray(data.filters) ? data.filters : [])
      .map(row => dialogFilterRow(row, rowFields));
    this.editingNodeId = model.id;
    this.openModalForEdit({
      eventType: data.eventType || '',
      filters,
      filtersOp: conditionOp(this.rule && this.rule.filtersOp),
    }, formDefinition);
  }

  openAddAction() {
    if (!this.fieldDefRuleActions) return;
    this.pendingActionConnect = null;
    this.dialogMode = 'addAction';
    this.editingNodeId = null;
    this.openModalForAdd(actionFormDefinition(this.fieldDefRuleActions, 'Add'));
  }

  openAddConnectedAction(sourceId, sourceHandle, position) {
    if (!this.fieldDefRuleActions || !sourceId) return;
    this.pendingActionConnect = {
      sourceId,
      sourceHandle,
      position,
    };
    this.dialogMode = 'addAction';
    this.editingNodeId = null;
    this.openModalForAdd(actionFormDefinition(this.fieldDefRuleActions, 'Add'));
  }

  openActionEditor(node) {
    if (!node || node.type !== NODE_TYPE_ACTION || !this.fieldDefRuleActions) return;
    const model = this.nodeById(node.id);
    if (!model) return;
    const title = this.fieldDefRuleActions.title || 'Edit';
    this.dialogMode = 'editAction';
    this.editingNodeId = model.id;
    this.openModalForEdit(actionRecord(model.data, this.fieldDefRuleActions), actionFormDefinition(this.fieldDefRuleActions, title));
  }

  onCanvasNodeClick = (event, node) => {
    if (!node) return;
    if (node.type === NODE_TYPE_TRIGGER) {
      this.openTriggerEditor(node);
      return;
    }
    if (node.type === NODE_TYPE_ACTION) {
      this.openActionEditor(node);
    }
  }

  submitAction(formData, formType, caller) {
    const data = actionDataFromForm(formData, formType || (this.dialogMode === 'addAction' ? 'add' : 'edit'));
    if (this.dialogMode === 'addAction') {
      const pending = this.pendingActionConnect;
      this.pendingActionConnect = null;
      const next = pending
        ? addConnectedActionNode(this.graph, pending.sourceId, pending.sourceHandle, data, pending.position)
        : addActionNode(this.graph, data);
      if (next !== this.graph) {
        this.graph = next;
        this.dirty = true;
      }
    } else {
      const node = this.nodeById(this.editingNodeId);
      if (!node) {
        if (caller) caller.setState({ inProcess: false });
        return;
      }
      node.data = data;
      this.dirty = true;
    }
    this.editingNodeId = null;
    this.dialogMode = null;
    this.syncCanvas();
    if (caller) caller.setState({ inProcess: false, success: true, message: '' });
    this.setState({ modalOpen: false });
  }

  settingsFormDefinition() {
    const copy = this.fieldDef
      ? JSON.parse(JSON.stringify(this.fieldDef))
      : { steps: [{ fields: [] }] };
    copy.title = 'Settings';
    if (!Array.isArray(copy.steps) || !copy.steps[0]) {
      copy.steps = [{ fields: [] }];
    }
    if (!Array.isArray(copy.steps[0].fields)) {
      copy.steps[0].fields = [];
    }
    const advancedFieldsEnabled = AppHelper.isSettingEnabled(this.orgId, 'AUTOMATIONS_ADVANCED_FIELDS');
    copy.steps[0].fields = copy.steps[0].fields.filter(field => {
      if (!field.advanced) return true;
      const matchKey = field._uiKey ? field._uiKey : field.key;
      if (matchKey === 'filtersOp' || matchKey === 'stopsOp') return false;
      return advancedFieldsEnabled;
    });
    copy.steps[0].fields = copy.steps[0].fields.filter(field => {
      if (!field) return false;
      const matchKey = field._uiKey ? field._uiKey : field.key;
      return matchKey !== 'filtersOp';
    });
    const iTypeField = Helper.getField(copy, 'iType');
    if (iTypeField) iTypeField.hidden = true;
    const eventTypeField = Helper.getField(copy, 'eventType');
    if (eventTypeField) {
      eventTypeField.label = 'Trigger Event';
      eventTypeField.placement = 'left';
    }
    if (this.fieldDefRuleStops) {
      const rowFields = entryConditionRowFields(this.fieldDefRuleStops);
      rowFields.forEach(field => {
        if (!field || field.key !== 'order') return;
        if (field.defaultValue == null || field.defaultValue === '') field.defaultValue = '010';
      });
      copy.steps[0].fields = copy.steps[0].fields.filter(field => {
        if (!field) return false;
        const matchKey = field._uiKey ? field._uiKey : field.key;
        return matchKey !== 'stopsOp';
      });
      copy.steps[0].fields.push({
        key: 'stopsOp',
        type: 'edit_text',
        hidden: true,
      });
      copy.steps[0].fields.push({
        key: 'stops',
        type: 'list',
        label: 'Exit Conditions',
        listConfig: {
          title: 'Exit Conditions',
          addButtonText: 'Add exit condition',
          minItems: 0,
          flatStorage: false,
          additionalListActions: conditionOpHeaderActions('stopsOp'),
          rowFields,
        },
      });
    }
    return copy;
  }

  openSettings() {
    if (!this.rule) return;
    this.dialogMode = 'settings';
    this.editingNodeId = null;
    const formDefinition = this.settingsFormDefinition();
    const steps = formDefinition.steps;
    const fields = steps && steps[0] && steps[0].fields;
    const listField = fields && fields.find(field => field && field.key === 'stops');
    const rowFields = listField && listField.listConfig ? listField.listConfig.rowFields : [];
    const current = Object.assign({}, this.rule, {
      stops: stopRowsForSettings(this.graph, this.rule, rowFields),
      stopsOp: conditionOp(this.rule.stopsOp),
    });
    this.openModalForEdit(current, formDefinition);
  }

  putRule(jsonBody) {
    return ApiHelper.callAwait({
      method: 'PUT',
      endPoint: this.ruleEndPoint(),
      jsonBody: Object.assign({ orgId: this.orgId }, jsonBody || {}),
    });
  }

  graphForSettings(formData) {
    const hasEventType = formData && Object.prototype.hasOwnProperty.call(formData, 'eventType');
    if (!hasEventType) return this.graph;
    const eventType = formData.eventType || '';
    const trigger = this.triggerNode();
    const currentEvent = trigger && trigger.data ? (trigger.data.eventType || '') : '';
    if (eventType === currentEvent) return this.graph;
    return graphWithTriggerEvent(this.graph, eventType);
  }

  applySettingsRefresh(rule, savedGraph) {
    if (rule) this.rule = rule;
    if (rule && rule.botJson) {
      this.setGraphFromRule(rule);
    } else if (savedGraph) {
      this.graph = savedGraph;
    }
    this.applyFieldDefs(rule);
    this.dirty = false;
    if (rule && rule.title) {
      this.pageTitle = rule.title;
      this.pageKey = rule.title;
    } else {
      this.pageTitle = 'Automation Journey';
      this.pageKey = this.pageTitle;
    }
    this.setHeaderActions();
    this.syncCanvas();
  }

  async submitSettings(formData, caller) {
    if (this.saving) {
      if (caller) caller.setState({ inProcess: false });
      return;
    }
    if (this.graphSaveBlocked(caller)) return;
    const savedFilters = this.filtersByEventType;
    const nextGraph = this.graphForSettings(formData);
    const detail = Object.assign({}, formData || {});
    delete detail.stops;
    const sanitized = sanitizeAutomationDetailFormPayload(detail, 'edit');
    delete sanitized.stops;
    delete sanitized.entityAction;
    delete sanitized.entityAttrName;
    const requestId = this.fieldDefRequestId + 1;
    this.fieldDefRequestId = requestId;
    this.saving = true;
    this.setState({ saving: true });
    try {
      const exitConditions = this.fieldDefRuleStops
        ? exitConditionsFromForm(formData && formData.stops)
        : null;
      const stopsOp = formData && Object.prototype.hasOwnProperty.call(formData, 'stopsOp')
        ? conditionOp(formData.stopsOp)
        : conditionOp(this.rule && this.rule.stopsOp);
      const stamped = applyBotJsonFields(nextGraph, {
        filtersOp: conditionOp(this.rule && this.rule.filtersOp),
        stopsOp,
        exitConditions,
      });
      await this.putRule(Object.assign({}, sanitized, {
        botJson: botJsonFromAutomation(stamped),
        filtersOp: conditionOp(this.rule && this.rule.filtersOp),
      }));
      const rule = await ApiHelper.callAwait({
        method: 'GET',
        endPoint: this.ruleEndPoint({ pb: 'fieldDef,fieldDefRuleFilters,fieldDefRuleStops,fieldDefRuleActions' }),
      });
      if (this.fieldDefRequestId !== requestId) {
        this.saving = false;
        this.dialogMode = null;
        if (caller) caller.setState({ inProcess: false, success: true, message: '' });
        this.setState({
          modalOpen: false,
          saving: false,
          showToast: true,
          isErrorToast: false,
          toastMsg: 'Automation settings saved.',
        });
        return;
      }
      this.filtersByEventType = savedFilters;
      this.applySettingsRefresh(rule, stamped);
      this.saving = false;
      this.dialogMode = null;
      if (caller) caller.setState({ inProcess: false, success: true, message: '' });
      this.setState({
        modalOpen: false,
        saving: false,
        showToast: true,
        isErrorToast: false,
        toastMsg: 'Automation settings saved.',
      });
    } catch (err) {
      this.saving = false;
      if (this.fieldDefRequestId === requestId) this.filtersByEventType = savedFilters;
      if (caller) caller.setState({ inProcess: false, success: false, message: Helper.getErrorMsg(err) });
      this.setState({ saving: false });
    }
  }

  customSubmit(options) {
    const formData = options && options.formData;
    const caller = options && options.caller;
    const formType = options && options.formType;
    if (this.dialogMode === 'settings') {
      this.submitSettings(formData, caller);
      return;
    }
    if (this.dialogMode === 'changeTrigger') {
      this.submitChangeTrigger(formData, caller);
      return;
    }
    if (this.dialogMode === 'addAction' || this.dialogMode === 'editAction') {
      this.submitAction(formData, formType, caller);
      return;
    }
    const node = this.nodeById(this.editingNodeId);
    if (!node) {
      if (caller) caller.setState({ inProcess: false });
      return;
    }
    const data = node.data || {};
    const filters = (Array.isArray(formData && formData.filters) ? formData.filters : []).map(persistFilterRow).filter(Boolean);
    let nextOp = conditionOp(data.filtersOp);
    if (this.rule && formData && Object.prototype.hasOwnProperty.call(formData, 'filtersOp')) {
      nextOp = conditionOp(formData.filtersOp);
      if (conditionOp(this.rule.filtersOp) !== nextOp) this.dirty = true;
      this.rule.filtersOp = nextOp;
    }
    node.data = {
      ...data,
      filtersOp: nextOp,
      filters: withRowOrders(filters),
    };
    this.editingNodeId = null;
    this.dialogMode = null;
    this.dirty = true;
    this.syncCanvas();
    if (caller) caller.setState({ inProcess: false, success: true, message: '' });
    this.setState({ modalOpen: false });
  }

  saveJourney() {
    if (!this.dirty || this.saving || this.state.saving || !this.graph || !this.ruleId) return;
    if (this.graphSaveBlocked()) return;
    const botJsonGraph = applyBotJsonFields(this.graph, {
      filtersOp: conditionOp(this.rule && this.rule.filtersOp),
      stopsOp: conditionOp(this.rule && this.rule.stopsOp),
    });
    this.graph = botJsonGraph;
    this.syncCanvas();
    const botJson = botJsonFromAutomation(botJsonGraph);
    this.saving = true;
    this.setState({ saving: true });
    ApiHelper.call(
      {
        method: 'PUT',
        endPoint: this.ruleEndPoint(),
        jsonBody: { botJson, filtersOp: conditionOp(this.rule && this.rule.filtersOp) },
      },
      (err) => {
        this.saving = false;
        if (err) {
          this.setState({
            saving: false,
            showToast: true,
            isErrorToast: true,
            toastMsg: Helper.getErrorMsg(err),
          });
          return;
        }
        this.dirty = false;
        this.setState({
          saving: false,
          showToast: true,
          isErrorToast: false,
          toastMsg: 'Automation journey saved.',
        });
        this.reloadRuleGraph().then(() => this.setState({})).catch(e => this.showSaveError(e));
      }
    );
  }

  onNodeDragStop = (nodeId, posX, posY) => {
    if (!this.graph) return;
    const next = updateNodePosition(this.graph, nodeId, posX, posY);
    if (next === this.graph) return;
    this.graph = next;
    this.dirty = true;
    this.syncCanvas();
    this.setState({});
  }

  onConnect = (params) => {
    if (!params || !this.graph) return;
    const next = addEdge(this.graph, params.source, params.target, params.sourceHandle);
    if (next === this.graph) return;
    this.graph = next;
    this.dirty = true;
    this.syncCanvas();
    this.setState({});
  }

  onEdgeDelete = (edgeId) => {
    if (!this.graph) return;
    const next = removeEdge(this.graph, edgeId);
    if (next === this.graph) return;
    this.graph = next;
    this.dirty = true;
    this.syncCanvas();
    this.setState({});
  }

  onNodeDelete = (nodeId) => {
    if (!this.graph) return;
    const next = removeNode(this.graph, nodeId);
    if (next === this.graph) return;
    this.graph = next;
    this.dirty = true;
    this.syncCanvas();
    this.setState({});
  }

  onNodeDuplicate = (nodeId) => {
    if (!this.graph) return;
    const next = duplicateNode(this.graph, nodeId);
    if (next === this.graph) return;
    this.graph = next;
    this.dirty = true;
    this.syncCanvas();
    this.setState({});
  }

  onAutoLayout = (next) => {
    if (!this.graph || next === this.graph) return;
    this.graph = next;
    this.dirty = true;
    this.syncCanvas();
    this.setState({});
  }

  renderAboveTable() {
    const saveDisabled = !this.dirty || this.saving || this.graphLoadError;
    let saveTooltip = 'Save';
    if (this.graphLoadError) saveTooltip = GRAPH_LOAD_ERROR_MSG;
    else if (this.saving) saveTooltip = 'Saving…';
    else if (!this.dirty) saveTooltip = 'No changes to save';
    return (
      <div className={css(Styles.containerNarrow)}>
        <div className={css(Styles.builderCard)}>
          <AutomationNodePalette
            onAddAction={() => this.openAddAction()}
            onChangeTrigger={() => this.openChangeTrigger()}
            onSave={() => this.saveJourney()}
            saveDisabled={saveDisabled}
            saveTooltip={saveTooltip}
          />
          <div className={css(Styles.canvasWrap)}>
            <AutomationFlowCanvas
              nodes={this.flowNodes}
              edges={this.flowEdges}
              onNodeClick={this.onCanvasNodeClick}
              onNodeDragStop={this.onNodeDragStop}
              onConnect={this.onConnect}
              onAddConnectedAction={this.fieldDefRuleActions
                ? (sourceId, sourceHandle, position) => this.openAddConnectedAction(sourceId, sourceHandle, position)
                : undefined}
              onEdgeDelete={this.onEdgeDelete}
              onNodeDelete={this.onNodeDelete}
              onNodeDuplicate={this.onNodeDuplicate}
              graph={this.graph}
              onAutoLayout={this.onAutoLayout}
            />
          </div>
        </div>
        <ChatbotConfirmDialog
          isOpen={!!this.state.leaveConfirmOpen}
          onGoBack={this.closeLeaveConfirm}
          onDiscard={this.discardLeaveConfirm}
        />
        <PageDialog
          PageComponent={AutomationResultsTable}
          PageComponentProps={{
            report: generateRuleJobsReport({
              ruleJobs: this.ruleJobs,
              ruleActions: this.rule && this.rule.actions,
              wideCells: true,
            }),
            maxRows: this.ruleJobsLimit,
            nextPage: this.ruleJobsNextPage,
            nextPageState: this.state.executionStatusNextPageState || ApiHelper.State.READY,
            onNext: () => this.onExecutionStatusNextPage(),
            cellWidths: {
              Index: Styles.cellWidthSmall,
              Info: Styles.cellWidthWide,
              Status: Styles.cellWidthWide,
              Actions: Styles.cellWidthSmall,
            },
            tableRowActions: (headers, rows) => this.executionStatusTableActions(headers, rows),
          }}
          title='Results'
          isOpen={!!this.state.resultsOpen}
          fullWidth
          maxWidth='md'
          onClosed={() => this.setState({ resultsOpen: false })}
        />
      </div>
    )
  }

  async onExecutionStatusNextPage() {
    if (!this.ruleJobsNextPage || this.state.executionStatusNextPageState === ApiHelper.State.LOADING) {
      return;
    }
    this.setState({ executionStatusNextPageState: ApiHelper.State.LOADING });
    try {
      const ruleJobsResult = await ApiHelper.callAwait({
        method: 'GET',
        endPoint: ApiHelper.makeUrlPath2({
          ms: this.ms,
          paths: ['rulejobs'],
          queryParams: {
            orgId: this.orgId,
            ruleId: this.ruleId,
            nextPage: this.ruleJobsNextPage,
            limit: this.ruleJobsLimit,
          },
        }),
      });
      const nextItems = (ruleJobsResult && Array.isArray(ruleJobsResult.items)) ? ruleJobsResult.items : [];
      this.ruleJobs = (this.ruleJobs || []).concat(nextItems);
      this.ruleJobsNextPage = ruleJobsResult && ruleJobsResult.nextPage;
    } catch (err) {
      this.showSaveError(err);
    } finally {
      this.setState({ executionStatusNextPageState: ApiHelper.State.READY });
    }
  }

  executionStatusTableActions(headers, rows) {
    let entityId = null;
    let entityType = null;
    if (Array.isArray(rows) && rows[3]) {
      entityId = rows[3].entityId && CRMHelper.getUserIdFromEnityId(rows[3].entityId);
      entityType = rows[3].entityType;
    }
    const actions = [];
    if (entityId && entityType === 'CRMCONTACT') {
      actions.push(
        <Tooltip title='View Contact' key='view-contact' classes={{ popper: css(Styles.tooltip) }}>
          <IconButton
            disableRipple
            className={css(AppStyles.entityTableLinkIcon)}
            onClick={() => window.open('/admin/orgs/' + this.orgId + '/crm-contacts/' + entityId, '_blank')}
          >
            <PersonOutlineOutlinedIcon style={{ fontSize: 26 }} />
          </IconButton>
        </Tooltip>
      );
    }
    return actions;
  }
}

const Styles = StyleSheet.create({
  containerNarrow: {
    width: '100%',
    paddingBottom: 50,
    boxSizing: 'border-box',
  },
  builderCard: {
    width: '100%',
    height: 'calc(100vh - ' + (AppTheme.headerbarHeight + 50) + 'px)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: AppTheme.secondaryBg,
    border: '1px solid ' + AppTheme.borderColor,
    borderRadius: AppTheme.borderRadius,
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  canvasWrap: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    position: 'relative',
    backgroundColor: AppTheme.secondaryBg,
  },
  cellWidthSmall: {
    width: '10%',
    padding: '8px 5px',
  },
  cellWidthWide: {
    width: '50%',
    padding: '8px 5px',
  },
  tooltip: {
    zIndex: 9999999999,
  },
  filtersOpActionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    marginRight: 8,
  },
  filtersOpActionLabel: {
    marginRight: 8,
  },
});
